import React, { useState, useEffect } from "react";
import { Row, Col, Card, Space, Statistic, Alert } from "antd";
import {
  InboxOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import { usePickupLists } from "../../../hooks/bot/usePickupLists";
import { useAssignments } from "../../../hooks/bot/useAssignments";
import PickupListSearchBar from "../../../components/bot/warehouse/PickupListSearchBar";
import WarehouseFilters from "../../../components/bot/warehouse/WarehouseFilters";
import PickupListsTable from "../../../components/bot/warehouse/PickupListsTable";
import PickupListDetailsDrawer from "./PickupListDetailsDrawer";

const WarehouseManagement = () => {
  const {
    filteredPickupLists,
    loading,
    searchText,
    setSearchText,
    filterStatus,
    setFilterStatus,
    fetchPickupLists,
    markPrepared,
    notifyParent,
  } = usePickupLists();

  const { fetchAssignments } = useAssignments();

  const [detailsDrawerVisible, setDetailsDrawerVisible] = useState(false);
  const [selectedPickupList, setSelectedPickupList] = useState(null);

  useEffect(() => {
    fetchPickupLists();
    fetchAssignments();
  }, [fetchPickupLists, fetchAssignments]);

  const handleViewDetails = (pickupList) => {
    setSelectedPickupList(pickupList);
    setDetailsDrawerVisible(true);
  };

  const handleMarkPrepared = async (pickupListId, notes) => {
    try {
      const success = await markPrepared(pickupListId, notes);
      if (success) {
        await fetchPickupLists();
      }
    } catch (error) {
      console.error("Error marking as prepared:", error);
    }
  };

  const handleNotifyParent = async (pickupListId) => {
    try {
      const success = await notifyParent(pickupListId);
      if (success) {
        await fetchPickupLists();
      }
    } catch (error) {
      console.error("Error notifying parent:", error);
    }
  };

  const stats = {
    pending: filteredPickupLists.filter((pl) => pl.status === "Pending").length,
    ready: filteredPickupLists.filter(
      (pl) => pl.status === "Ready" || pl.status === "Notified",
    ).length,
    completed: filteredPickupLists.filter((pl) => pl.status === "Completed")
      .length,
  };

  return (
    <div style={{ padding: "24px" }}>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card>
            <Space direction="vertical" style={{ width: "100%" }} size="large">
              <h2 style={{ margin: 0 }}>
                <InboxOutlined /> Warehouse Management
              </h2>

              <Row gutter={16}>
                <Col xs={24} sm={8}>
                  <Card>
                    <Statistic
                      title="Pending Preparation"
                      value={stats.pending}
                      prefix={<ClockCircleOutlined />}
                      valueStyle={{ color: "#faad14" }}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={8}>
                  <Card>
                    <Statistic
                      title="Ready for Pickup"
                      value={stats.ready}
                      prefix={<CheckCircleOutlined />}
                      valueStyle={{ color: "#1890ff" }}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={8}>
                  <Card>
                    <Statistic
                      title="Completed"
                      value={stats.completed}
                      prefix={<CheckCircleOutlined />}
                      valueStyle={{ color: "#52c41a" }}
                    />
                  </Card>
                </Col>
              </Row>
            </Space>
          </Card>
        </Col>

        <Col span={24}>
          <Card>
            <Space direction="vertical" style={{ width: "100%" }}>
              <PickupListSearchBar
                searchText={searchText}
                onSearch={setSearchText}
              />
              <WarehouseFilters
                filterStatus={filterStatus}
                onStatusChange={setFilterStatus}
              />
            </Space>
          </Card>
        </Col>

        <Col span={24}>
          <Card>
            {!loading &&
            filteredPickupLists.length === 0 &&
            !searchText &&
            !filterStatus ? (
              <Alert
                message="No Pickup Lists Yet"
                description="Pickup lists are automatically created when students complete payment for their BOT items. Once created, they will appear here for preparation and tracking."
                type="info"
                showIcon
                style={{ marginBottom: "20px" }}
              />
            ) : null}

            <PickupListsTable
              pickupLists={filteredPickupLists}
              loading={loading}
              onViewDetails={handleViewDetails}
              onMarkPrepared={handleMarkPrepared}
              onNotifyParent={handleNotifyParent}
            />
          </Card>
        </Col>
      </Row>

      <PickupListDetailsDrawer
        visible={detailsDrawerVisible}
        pickupList={selectedPickupList}
        onClose={() => {
          setDetailsDrawerVisible(false);
          setSelectedPickupList(null);
        }}
        onRefresh={fetchPickupLists}
      />
    </div>
  );
};

export default WarehouseManagement;
